export interface AgentPresence {
  attached: boolean
  expiresAt: number
  clientName?: string | null
}

export type AgentActivityAction = 'created' | 'updated' | 'deleted'

export type AgentActivityPhase = 'before' | 'after'

export type AgentActivityTarget = 'node' | 'module' | 'interface'

export interface AgentActivity {
  action: AgentActivityAction
  section: string
  phase?: AgentActivityPhase
  target?: AgentActivityTarget
  nodeIds?: string[]
  moduleId?: string | null
  interfaceId?: string | null
}

export type FollowTarget =
  | { kind: 'node'; nodeId: string; moduleId?: string | null }
  | { kind: 'module'; moduleId: string }
  | { kind: 'interface'; interfaceId: string }

export interface ScreenState {
  pathname: string
  moduleId?: string
  selectedNodeId?: string
  defaultInterfaceId?: string
}

export interface UserView {
  section: string
  url: string
  module_id: string | null
  node_id: string | null
  selected_node_ids: string[]
  interface_id: string | null
  component_id: string | null
  edit_mode: boolean
  has_unconfirmed_changes: boolean
}

export const presenceFrom = (
  attached: boolean,
  expiresInSeconds: number,
  clientName: string | null | undefined,
  now: number
): AgentPresence => ({
  attached: attached && expiresInSeconds > 0,
  expiresAt: now + expiresInSeconds * 1000,
  clientName: clientName ?? null
})

export const isAgentAttached = (presence: AgentPresence | undefined, now: number): boolean =>
  !!presence?.attached && presence.expiresAt > now

export const followTargetOf = (activity: AgentActivity): FollowTarget | undefined => {
  if (activity.section === 'interfaces') {
    return activity.interfaceId
      ? { kind: 'interface', interfaceId: activity.interfaceId }
      : undefined
  }
  if (activity.section !== 'code') {
    return undefined
  }
  const [nodeId] = activity.nodeIds ?? []
  if (activity.target === 'module') {
    return activity.moduleId ? { kind: 'module', moduleId: activity.moduleId } : undefined
  }
  if (activity.action !== 'deleted' && nodeId) {
    return { kind: 'node', nodeId, moduleId: activity.moduleId }
  }
  return activity.moduleId ? { kind: 'module', moduleId: activity.moduleId } : undefined
}

export const followTargetKey = (target: FollowTarget): string => {
  switch (target.kind) {
    case 'node':
      return `node:${target.nodeId}`
    case 'module':
      return `module:${target.moduleId}`
    case 'interface':
      return `interface:${target.interfaceId}`
  }
}

export const followTargetLink = (target: FollowTarget): string => {
  switch (target.kind) {
    case 'node':
      return `/code/go-to?nodeId=${encodeURIComponent(target.nodeId)}`
    case 'module':
      return `/code/go-to?moduleId=${encodeURIComponent(target.moduleId)}`
    case 'interface':
      return `/interfaces/${target.interfaceId}`
  }
}

const INTERFACE_ROUTE = /^\/interfaces\/([^/?#]+)/
const HOME_INTERFACE_ROUTE_ID = 'home'
export const MAX_REPORTED_SELECTED_NODE_IDS = 200

const safeDecodeURIComponent = (value: string): string => {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

export const displayedInterfaceId = (
  pathname: string,
  defaultInterfaceId?: string
): string | undefined => {
  const match = INTERFACE_ROUTE.exec(pathname)
  if (!match) {
    return undefined
  }
  return match[1] === HOME_INTERFACE_ROUTE_ID
    ? defaultInterfaceId
    : safeDecodeURIComponent(match[1])
}

export const isAlreadyShowing = (target: FollowTarget, screen: ScreenState): boolean => {
  switch (target.kind) {
    case 'node':
      return screen.pathname === '/code' && screen.selectedNodeId === target.nodeId
    case 'module':
      return screen.pathname === '/code' && screen.moduleId === target.moduleId
    case 'interface':
      return displayedInterfaceId(screen.pathname, screen.defaultInterfaceId) === target.interfaceId
  }
}

const sectionOf = (pathname: string): string => pathname.split('/').filter(Boolean)[0] ?? 'home'

export const buildUserView = ({
  pathname,
  href,
  moduleId,
  selectedNodeId,
  selectedNodeIds,
  selectedComponentId,
  editMode,
  hasUnconfirmedChanges,
  defaultInterfaceId
}: {
  pathname: string
  href: string
  moduleId?: string
  selectedNodeId?: string
  selectedNodeIds?: string[]
  selectedComponentId?: string
  editMode: boolean
  hasUnconfirmedChanges: boolean
  defaultInterfaceId?: string
}): UserView => {
  const section = sectionOf(pathname)
  const inCode = section === 'code'
  const interfaceId = displayedInterfaceId(pathname, defaultInterfaceId)
  return {
    section,
    url: href,
    module_id: inCode ? moduleId ?? null : null,
    node_id: inCode ? selectedNodeId ?? null : null,
    selected_node_ids: inCode
      ? (selectedNodeIds ?? []).slice(0, MAX_REPORTED_SELECTED_NODE_IDS)
      : [],
    interface_id: interfaceId ?? null,
    component_id: interfaceId ? selectedComponentId ?? null : null,
    edit_mode: !!interfaceId && editMode,
    has_unconfirmed_changes: hasUnconfirmedChanges
  }
}

/** Where the browser last followed the agent to, and what it last told the user about. */
export interface FollowMemory {
  followedKey?: string
  toastedKey?: string
}

export type AgentActivityDecision = 'follow' | 'toast' | 'none'

/**
 * Follows the agent only when it moves to a different place: once the user has
 * been there, leaving it on their own is respected until the agent moves on.
 */
export const decideAgentActivity = (
  key: string,
  {
    alreadyShowing,
    canFollow,
    beforeChange = false
  }: { alreadyShowing: boolean; canFollow: boolean; beforeChange?: boolean },
  memory: FollowMemory
): { decision: AgentActivityDecision; memory: FollowMemory } => {
  if (alreadyShowing) {
    return { decision: 'none', memory: { ...memory, followedKey: key } }
  }
  if (beforeChange && !canFollow) {
    return { decision: 'none', memory }
  }
  if (canFollow) {
    return key === memory.followedKey
      ? { decision: 'none', memory }
      : { decision: 'follow', memory: { ...memory, followedKey: key } }
  }
  return key === memory.toastedKey
    ? { decision: 'none', memory }
    : { decision: 'toast', memory: { ...memory, toastedKey: key } }
}

export const forgetFollowedTarget = (memory: FollowMemory): FollowMemory => ({
  ...memory,
  followedKey: undefined
})
